import { InformationCircleIcon } from '@heroicons/react/outline';
import { Tooltip } from '@yoast/ui-library';
import {
  useState,
  type ComponentType,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

type TooltipUncontrolledProps = {
  Icon?: ComponentType<{ className?: string }>;
  className?: string;
  children?: ReactNode;
  id?: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
} & HTMLAttributes<HTMLDivElement>;

/**
 * Info icon with a hover tooltip, positioned inline by the library's `Tooltip`.
 *
 * Note: because that positioning is `absolute`, the tooltip is clipped if an
 * ancestor scrolls or hides overflow (hence the `yst-overflow-visible` overrides
 * at several call sites). For triggers inside a scroll container, use
 * `InfoTooltip`, which portals the bubble out of the way instead.
 */
export default function TooltipUncontrolled({
  Icon = InformationCircleIcon,
  className = '',
  children,
  position = 'left',
  id = '',
  ...props
}: TooltipUncontrolledProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  if (!children) return null;

  return (
    <div
      className={className || 'yst-relative'}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      aria-describedby={id ? 'tooltip-button-id' : 'tooltip-button'}
      {...props}
    >
      <Icon className="yst-relative yst-h-6 yst-w-6 yst-text-slate-400" />
      {showTooltip && (
        <Tooltip position={position} id={id || 'tooltip-button'}>
          {children}
        </Tooltip>
      )}
    </div>
  );
}
